import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Stack,
  Typography,
  Autocomplete,
  Chip,
} from "@mui/material";
import {
  type PlotRequestTag,
  type SavedPlotFilterValues,
} from "@/features/map/types";
import { useIsMobile } from "@/hooks/useIsMobile";

interface IProps {
  open: boolean;
  onClose: () => void;
  availableTags: PlotRequestTag[];
  initialFilters?: SavedPlotFilterValues;
  onApply: (filters: SavedPlotFilterValues) => void;
}

export const SavedPlotFilterModal: React.FC<IProps> = ({
  open,
  onClose,
  availableTags,
  initialFilters,
  onApply,
}) => {
  const isMobile = useIsMobile();
  const [filters, setFilters] = useState<SavedPlotFilterValues>({
    name: null,
    countFrom: null,
    countTo: null,
    createdAtFrom: null,
    createdAtTo: null,
    updatedAtFrom: null,
    updatedAtTo: null,
    tags: [],
  });

  useEffect(() => {
    if (initialFilters) {
      setFilters(initialFilters);
    }
  }, [initialFilters, open]);

  const handleApply = (): void => {
    onApply(filters);
    onClose();
  };

  const handleClose = (
    _: Record<string, unknown>,
    reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    // 入力中の誤タップ・Esc で閉じない。閉じるのは「閉じる」「絞り込む」だけ
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6">フィルター条件</Typography>
          <Button variant="outlined" onClick={onClose}>
            × 閉じる
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {/* 識別名 */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              識別名
            </Typography>
            <TextField
              placeholder="識別名を入力"
              value={filters.name ?? ""}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  name: e.target.value || null,
                });
              }}
              fullWidth
            />
          </Box>

          {/* 件数 */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              件数
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                placeholder="以上"
                type="number"
                inputProps={{ inputMode: "numeric", pattern: "\\d*" }}
                value={filters.countFrom ?? ""}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    countFrom: e.target.value ? Number(e.target.value) : null,
                  });
                }}
                fullWidth
              />
              <Typography>〜</Typography>
              <TextField
                placeholder="以下"
                type="number"
                inputProps={{ inputMode: "numeric", pattern: "\\d*" }}
                value={filters.countTo ?? ""}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    countTo: e.target.value ? Number(e.target.value) : null,
                  });
                }}
                fullWidth
              />
            </Stack>
          </Box>

          {/* タグ（複数選択可） */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              タグ（複数選択可）
            </Typography>
            <Autocomplete
              multiple
              options={availableTags}
              getOptionLabel={(option) => option.name}
              value={filters.tags}
              onChange={(_, newValue) => {
                setFilters({
                  ...filters,
                  tags: newValue,
                });
              }}
              isOptionEqualToValue={(option, value) =>
                option.name === value.name
              }
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField {...params} placeholder="タグを選択" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    key={option.id}
                    size="small"
                  />
                ))
              }
            />
          </Box>

          {/* 作成日時 */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              作成日時
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="以降"
                type="datetime-local"
                value={filters.createdAtFrom ?? ""}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    createdAtFrom: e.target.value || null,
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
              <TextField
                label="以前"
                type="datetime-local"
                value={filters.createdAtTo ?? ""}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    createdAtTo: e.target.value || null,
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
            </Stack>
          </Box>

          {/* 更新日時 */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              更新日時
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="以降"
                type="datetime-local"
                value={filters.updatedAtFrom ?? ""}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    updatedAtFrom: e.target.value || null,
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
              <TextField
                label="以前"
                type="datetime-local"
                value={filters.updatedAtTo ?? ""}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    updatedAtTo: e.target.value || null,
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: "center" }}>
        <Button
          onClick={handleApply}
          color="primary"
          variant="contained"
          size="large"
          sx={{ minWidth: 120 }}
        >
          絞り込む
        </Button>
      </DialogActions>
    </Dialog>
  );
};
